import { Link } from 'react-router-dom';
import { ProductHeader } from '@/components/auth/ProductHeader';
import { ProductFooter } from '@/components/auth/ProductFooter';
import '@/modules/auth/auth.css';
import './legal.css';

export function PrivacyPage() {
  return (
    <div className="auth-page auth-page--with-chrome">
      <ProductHeader product="assess" />
      <div className="legal-page">
        <div className="legal-container">
          <nav className="legal-breadcrumb">
            <Link to="/login">← Back to Login</Link>
          </nav>
          <h1 className="legal-title">Privacy Policy</h1>
          <p className="legal-updated">Last updated: 13 March 2026 · Effective: 13 March 2026</p>
          <p className="legal-updated" style={{ marginTop: '-0.5rem', marginBottom: '1.5rem' }}>
            Applicable law: IT Act 2000 · SPDI Rules 2011 · DPDP Act 2023 · Consumer Protection Act 2019
          </p>

          <div className="legal-highlight">
            Paazio does not sell your personal data. We collect only what is necessary to deliver our services.
            You may access, correct, or delete your data at any time. Our Grievance Officer is reachable at{' '}
            <a href="mailto:privacy@paazio.com">privacy@paazio.com</a>.
          </div>

          <section className="legal-section">
            <h2>1. Data Controller Information</h2>
            <p>
              <strong>Paazio</strong> ("we", "us", "our") is the data controller for personal data collected
              through Paazio Assess. Our registered office is in Chennai, Tamil Nadu, India.
            </p>
            <p>
              Contact: <a href="mailto:privacy@paazio.com">privacy@paazio.com</a> ·{' '}
              <a href="tel:+919790398729">+91 97903 98729</a>
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Information We Collect</h2>
            <p><strong>Account and Identity Data:</strong> Full name, email address, role (student, organiser, academy admin, grader). Optionally: profile photo.</p>
            <p><strong>Assessment Data:</strong> Assessment/test attempts, scores, time-on-question analytics, adaptive difficulty history, subjective answer submissions, code execution results.</p>
            <p><strong>Payment Data:</strong> Transaction history, plan purchases, wallet balances. Payment processing is handled by Razorpay — we do not store card numbers or UPI credentials.</p>
            <p><strong>Technical Data:</strong> IP address, browser/device type, access logs, feature usage patterns.</p>
            <p><strong>Proctoring Data (if enabled):</strong> Webcam snapshots, browser tab-switch events, AI integrity reports — collected only with your explicit consent before each assessment.</p>
            <p><strong>Support Communications:</strong> Support chat messages, notifications.</p>
          </section>

          <section className="legal-section">
            <h2>3. How We Use Your Information</h2>
            <ul>
              <li>Create and manage your account and authenticate your identity</li>
              <li>Administer assessments, tests, and adaptive assessments (CAT)</li>
              <li>Generate performance analytics, score reports, and certificates</li>
              <li>Process payments, manage credit wallets, and issue receipts</li>
              <li>Enable proctoring and assessment integrity monitoring (with consent)</li>
              <li>Send transactional communications (results, receipts, OTPs)</li>
              <li>Provide customer support and resolve disputes</li>
              <li>Detect fraud and policy violations</li>
              <li>Comply with applicable Indian laws and lawful authority requests</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. Legal Basis for Processing (DPDP Act 2023)</h2>
            <ul>
              <li><strong>Consent:</strong> Provided when you create your account. Withdrawable at any time by deleting your account.</li>
              <li><strong>Contractual Necessity:</strong> Required to deliver assessments, process payments, and issue results.</li>
              <li><strong>Legal Obligation:</strong> Required by the IT Act 2000, Income Tax Act, and other Indian regulations.</li>
              <li><strong>Legitimate Interest:</strong> Security monitoring, fraud prevention, platform improvement.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Data Sharing and Third Parties</h2>
            <p>We do <strong>not sell</strong> your personal data. We share data only with:</p>
            <ul>
              <li><strong>Razorpay:</strong> Payment processing. PCI-DSS compliant, RBI-regulated.</li>
              <li><strong>Cloud Storage (MinIO/S3-compatible):</strong> Assessment files and certificates, encrypted at rest.</li>
              <li><strong>Judge0 (Code Execution):</strong> For sandboxed execution of programming assessment submissions.</li>
              <li><strong>Email/SMS Providers:</strong> Transactional messages (OTPs, assessment results).</li>
              <li><strong>Academy Administrators:</strong> Assessment results and attendance limited to your enrolled academy.</li>
              <li><strong>Law Enforcement:</strong> Where required by valid legal order under Indian law.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Data Retention</h2>
            <ul>
              <li><strong>Account data:</strong> Active period + 3 years after deletion</li>
              <li><strong>Payment records:</strong> 7 years (Indian tax compliance)</li>
              <li><strong>Assessment results and history:</strong> Duration of enrolment + 2 years</li>
              <li><strong>Proctoring data:</strong> 30 days after assessment, extended for active dispute resolution</li>
              <li><strong>Support chat logs:</strong> 1 year from thread closure</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. Your Rights under the DPDP Act 2023</h2>
            <p>As a Data Principal, you have the right to:</p>
            <ul>
              <li><strong>Access:</strong> Request a summary of your personal data and how it is used</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data</li>
              <li><strong>Erasure:</strong> Request deletion of your data (subject to retention obligations)</li>
              <li><strong>Grievance Redressal:</strong> File a complaint with our Grievance Officer or the Data Protection Board of India</li>
              <li><strong>Nomination:</strong> Nominate another person to exercise your rights in case of death or incapacity</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent at any time; may affect service availability</li>
            </ul>
            <p>To exercise these rights, email <a href="mailto:privacy@paazio.com">privacy@paazio.com</a>. We respond within <strong>30 days</strong>.</p>
          </section>

          <section className="legal-section">
            <h2>8. Sensitive Personal Data (SPDI Rules 2011)</h2>
            <p>We process the following sensitive personal data only with your explicit consent:</p>
            <ul>
              <li><strong>Financial information:</strong> Handled exclusively by Razorpay</li>
              <li><strong>Biometric data (proctoring):</strong> Webcam images for identity verification, only when proctoring is enabled and with pre-assessment consent</li>
            </ul>
            <p>Passwords are stored as bcrypt hashes. We do not store plaintext passwords or raw payment credentials.</p>
          </section>

          <section className="legal-section">
            <h2>9. Children's Privacy</h2>
            <p>
              Paazio Assess is available to users aged <strong>18 and above</strong>, or to users under 18
              with verifiable parental or guardian consent, in compliance with the DPDP Act 2023.
              Institutions enrolling minors are responsible for obtaining and documenting parental consent.
              We do not knowingly process data from users under 18 without verifiable parental consent.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Cookies and Tracking</h2>
            <ul>
              <li><strong>Essential cookies:</strong> Authentication, JWT session management (cannot be disabled)</li>
              <li><strong>Session storage:</strong> Academy context, cleared when you close the tab</li>
              <li><strong>Local storage:</strong> Access tokens for authenticated sessions</li>
            </ul>
            <p>We do <strong>not</strong> use advertising cookies, tracking pixels, or third-party analytics that share your data externally.</p>
          </section>

          <section className="legal-section">
            <h2>11. Security Measures</h2>
            <ul>
              <li>TLS 1.3 encryption for all data in transit</li>
              <li>AES-256 encryption for data at rest</li>
              <li>Bcrypt password hashing (min. cost factor 12)</li>
              <li>Role-based access control (RBAC) with per-academy isolation</li>
              <li>Rate limiting on all authentication endpoints</li>
              <li>Data breach notification within 72 hours to affected users and authorities</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>12. Grievance Officer (IT Act / DPDP Act)</h2>
            <p>
              As required by the IT (SPDI) Rules 2011 and the DPDP Act 2023, our Grievance Officer is:
            </p>
            <p>
              <strong>Grievance Officer — Paazio</strong><br />
              📧 <a href="mailto:privacy@paazio.com">privacy@paazio.com</a><br />
              📞 <a href="tel:+919790398729">+91 97903 98729</a><br />
              Chennai, Tamil Nadu, India<br />
              <em>Acknowledgement within 48 hours · Resolution within 30 days</em>
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Changes to This Policy</h2>
            <p>
              Material changes will be communicated by email and in-platform notice at least 30 days before
              they take effect. Continued use after the effective date constitutes acceptance.
            </p>
          </section>

          <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', fontSize: '0.875rem', color: '#6b7280' }}>
            Related: <Link to="/terms">Terms of Service</Link>
          </div>
        </div>
      </div>
      <ProductFooter />
    </div>
  );
}
